import {
  AdminSpinner,
  Box,
  Button,
  Div,
  EditPenIcon,
  H3,
  H5,
  Icon,
  P,
  PlusIcon,
  Separator,
  SuccessToast,
  TrashIconRed,
  getBrandsWorkingHours,
  getLocationsWorkingHours,
  renderDayOfWeekString,
  updateBrandsWorkingHours,
  updateLocationsWorkingHours,
  reportError,
} from '@vgrubs/components';
import { SimpleBrandEntity } from 'components/src/types/brand.types';
import {
  SimpleLocationEntity,
  WorkingHoursReponseV2,
} from 'components/src/types/location.types';
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { convert24HourTo12Hour } from '../../../helpers/time-helpers';
import { AddWorkingHours } from '../AddWorkingHours';
import { ApplyToAllBrandsModal } from '../ApplyToAllBrandsModal';
import { DeleteWorkingHours } from '../DeleteWorkingHours';
import { normalizeHours, stableStringifyWorkingHours } from '../helpers';
import { useProviderSyncStatus } from 'components/src/store/useProviderSyncStatus';

export const WorkingHours = ({
  selectedBrand,
  selectedLocation,
}: {
  selectedBrand: SimpleBrandEntity | null;
  selectedLocation: SimpleLocationEntity | null;
}) => {
  const [loading, setLoading] = useState(false);

  const [workingHours, setWorkingHours] =
    useState<WorkingHoursReponseV2 | null>(null);

  const initialSnapshotRef = useRef<string>('{"business":[],"delivery":[]}');

  const [addDayModal, setAddDayModal] = useState<{
    type: 'business' | 'delivery';
    edit:
      | null
      | WorkingHoursReponseV2['business'][number]
      | WorkingHoursReponseV2['delivery'][number];
    index: number;
    multiple: boolean;
  } | null>(null);
  const [deleteDayModal, setDeleteDayModal] = useState<{
    index: number;
    type: 'business' | 'delivery';
  } | null>(null);
  const [applyToAllBrands, setApplyToAllBrands] = useState(false);

  async function getWorkingHours() {
    if (!selectedLocation) return;

    const locationOrBrandFunction = selectedBrand
      ? getBrandsWorkingHours
      : getLocationsWorkingHours;

    const locationOrBrandId = selectedBrand
      ? selectedBrand.id
      : selectedLocation.id;

    try {
      setLoading(true);
      const { data } = await locationOrBrandFunction(locationOrBrandId);
      setWorkingHours({
        business: normalizeHours(data.data.business),
        delivery: normalizeHours(data.data.delivery),
      });
      initialSnapshotRef.current = stableStringifyWorkingHours(data.data);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  // keep only globals from the store
  const { initializedProviderConnection, setInitializedProviderConnection } =
    useProviderSyncStatus();

  async function handleSave(applyToAllBrands: boolean) {
    if (!selectedLocation || !workingHours) return;

    const locationOrBrandFunction = selectedBrand
      ? updateBrandsWorkingHours
      : updateLocationsWorkingHours;

    const locationOrBrandId = selectedBrand
      ? selectedBrand.id
      : selectedLocation.id;

    const body = {
      business: workingHours.business,
      delivery: workingHours.delivery,
      applyToAllBrands: selectedBrand ? undefined : applyToAllBrands,
    };

    try {
      setLoading(true);
      const { data } = await locationOrBrandFunction(locationOrBrandId, body);
      SuccessToast(data.message);
      setWorkingHours({
        business: normalizeHours(data.data.business),
        delivery: normalizeHours(data.data.delivery),
      });
      initialSnapshotRef.current = stableStringifyWorkingHours(data.data);

      const haveWorkingHoursSet = data.data.business.length > 0;

      // ✅ update scoped statuses using latest snapshot from the store
      if (initializedProviderConnection) {
        const key = {
          locationId: selectedBrand ? 0 : selectedLocation.id,
          brandId: selectedBrand ? selectedBrand.id : 0,
        };

        const { statusesByScope, setStatusesFor } =
          useProviderSyncStatus.getState();

        const current =
          statusesByScope.find(
            (e) => e.locationId === key.locationId && e.brandId === key.brandId
          )?.statuses ?? [];

        const activeProvider =
          typeof initializedProviderConnection === 'object' &&
          initializedProviderConnection !== null &&
          'provider' in (initializedProviderConnection as any)
            ? (initializedProviderConnection as any).provider
            : initializedProviderConnection;

        const next = current.map((item) =>
          item.provider === activeProvider
            ? { ...item, haveWorkingHoursSet }
            : item
        );

        setStatusesFor(key, next);
      }

      setInitializedProviderConnection(null);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    getWorkingHours();
  }, [selectedLocation, selectedBrand]);

  const { business, delivery } = workingHours || {};

  const isDirty = useMemo(() => {
    return (
      stableStringifyWorkingHours(workingHours) !== initialSnapshotRef.current
    );
  }, [workingHours]);

  const handleCancel = () => {
    setWorkingHours(JSON.parse(initialSnapshotRef.current));
  };

  return (
    <>
      {loading && <AdminSpinner isRootPage />}
      <Div>
        {Array.isArray(business) && (
          <>
            <Div
              $flex
              $between
              $alignCenter
              $m="16px 0"
              $mSM="12px 0"
              $gap="16px"
            >
              <div>
                <H3 $mb="0">Pickup Hours</H3>
                {business.length < 1 && (
                  <P $m="8px 0 0 0">
                    This {selectedBrand ? 'brand' : 'location'} currently
                    doesn't have Pickup Hours.
                  </P>
                )}
              </div>

              {business?.length < 1 ? (
                <Button
                  $selected
                  $small
                  onClick={() =>
                    setAddDayModal({
                      type: 'business',
                      edit: null,
                      index: 0,
                      multiple: true,
                    })
                  }
                >
                  Add
                </Button>
              ) : (
                <Icon
                  $pointer
                  onClick={() =>
                    setAddDayModal({
                      type: 'business',
                      edit: null,
                      index: 0,
                      multiple: true,
                    })
                  }
                >
                  <PlusIcon />
                </Icon>
              )}
            </Div>

            {business?.map((workingHour, i) => (
              <Box $flex $between $alignCenter $mb="8" key={i}>
                <div>
                  <P $mb="4">{renderDayOfWeekString(workingHour.day)}</P>
                  <H5 $mb="0">
                    {convert24HourTo12Hour(workingHour.startTime)} -{' '}
                    {convert24HourTo12Hour(workingHour.endTime)}
                  </H5>
                </div>

                <Div $flex $between $alignCenter $gap="16px" $gapSM="8px">
                  <Icon
                    $shadow
                    $pointer
                    onClick={() =>
                      setAddDayModal({
                        type: 'business',
                        edit: workingHour,
                        index: i,
                        multiple: false,
                      })
                    }
                  >
                    <EditPenIcon />
                  </Icon>
                  <Icon
                    $pointer
                    onClick={() =>
                      setDeleteDayModal({ index: i + 1, type: 'business' })
                    }
                  >
                    <TrashIconRed />
                  </Icon>
                </Div>
              </Box>
            ))}
          </>
        )}

        <Separator $m="16px 0" />

        {Array.isArray(delivery) && (
          <>
            <Div
              $flex
              $between
              $alignCenter
              $m="16px 0"
              $mSM="12px 0"
              $gap="16px"
            >
              <div>
                <H3 $m="0">Delivery Hours</H3>
                {delivery.length < 1 && (
                  <P $m="8px 0 0 0">
                    This {selectedBrand ? 'brand' : 'location'} currently
                    doesn't have delivery hours.
                  </P>
                )}
              </div>

              {delivery?.length < 1 ? (
                <Button
                  $selected
                  $small
                  onClick={() =>
                    setAddDayModal({
                      type: 'delivery',
                      edit: null,
                      index: 0,
                      multiple: true,
                    })
                  }
                >
                  Add
                </Button>
              ) : (
                <Icon
                  $pointer
                  onClick={() =>
                    setAddDayModal({
                      type: 'delivery',
                      edit: null,
                      index: 0,
                      multiple: true,
                    })
                  }
                >
                  <PlusIcon />
                </Icon>
              )}
            </Div>

            {delivery?.map((workingHour, i) => (
              <Box $flex $between $alignCenter $mb="8" key={i}>
                <div>
                  <P $mb="4">{renderDayOfWeekString(workingHour.day)}</P>
                  <H5 $mb="0">
                    {convert24HourTo12Hour(workingHour.startTime)} -{' '}
                    {convert24HourTo12Hour(workingHour.endTime)}
                  </H5>
                </div>

                <Div $flex $between $alignCenter $gap="16px" $gapSM="8px">
                  <Icon
                    $shadow
                    $pointer
                    onClick={() =>
                      setAddDayModal({
                        type: 'delivery',
                        edit: workingHour,
                        index: i,
                        multiple: false,
                      })
                    }
                  >
                    <EditPenIcon />
                  </Icon>
                  <Icon
                    $pointer
                    onClick={() =>
                      setDeleteDayModal({ index: i + 1, type: 'delivery' })
                    }
                  >
                    <TrashIconRed />
                  </Icon>
                </Div>
              </Box>
            ))}
          </>
        )}
        <Separator $m="16px 0" />
      </Div>
      {isDirty && (
        <Div $flex $justifyEnd $m="20px 0" $mSM="16px 0" $gap="16px">
          <Button $selected $gray onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            $selected
            onClick={() =>
              selectedBrand ? handleSave(true) : setApplyToAllBrands(true)
            }
          >
            Save
          </Button>
        </Div>
      )}
      <AddWorkingHours
        modalIsOpen={addDayModal}
        closeModal={() => setAddDayModal(null)}
        setWorkingHours={
          setWorkingHours as Dispatch<SetStateAction<WorkingHoursReponseV2>>
        }
      />
      <DeleteWorkingHours
        modalIsOpen={deleteDayModal}
        closeModal={() => setDeleteDayModal(null)}
        setWorkingHours={
          setWorkingHours as Dispatch<SetStateAction<WorkingHoursReponseV2>>
        }
      />
      <ApplyToAllBrandsModal
        isOpen={applyToAllBrands}
        setIsOpen={setApplyToAllBrands}
        handleSubmit={(v) => {
          handleSave(v);
          setApplyToAllBrands(false);
        }}
        text="Do you want to apply this working hours setup to all brands connected to this location?"
      />
    </>
  );
};
